/**
 * Configures the generated @hey-api fetch client.
 *
 * All API calls target `/api/*`. In development, Vite proxies that prefix
 * to the FastAPI backend (see `vite.config.ts`); in production, nginx does
 * the same rewrite (see `nginx.conf`). The browser never knows the backend
 * URL — only the same-origin `/api` path.
 *
 * Importing this module has the side effect of configuring the singleton
 * client. `src/lib/api/index.ts` does this on first import.
 */

import { client } from "@/lib/api/generated/client.gen";
import { demoPersonaHeaders, isDemoPersonaInvalid } from "@/lib/demo-persona";
import { clearDemoPersona, getDemoPersona } from "@/lib/demo-persona-store";
import { isKioskNameRequired, kioskHeaders } from "@/lib/kiosk";
import { clearKioskPerson, getKioskPerson } from "@/lib/kiosk-person";
import { logger } from "@/lib/logger";
import {
  describeNetworkFailure,
  isAbort,
  isBackgroundSessionCheck,
  networkFailureAction,
  type NetworkFailureContext,
  READY_WAIT_MAX_MS,
  retryInfoOf,
  withNetworkRetry,
} from "@/lib/network-retry";

import { currentScreen, reportClientError } from "./client-error-report";
import { queryClient } from "./query-client";

/** Tells the API which screen a failure happened on, for the auto-logged feedback. */
const SCREEN_HEADER = "X-Screen";

/**
 * Structured error thrown by all SDK calls.
 *
 * @hey-api/client-fetch with `throwOnError: true` throws just the parsed
 * response body by default, which loses the HTTP status. The error
 * interceptor below wraps every non-OK response in this shape so callers
 * can branch on `status` (e.g. 401 vs 403 vs 400) and read FastAPI's
 * `{ detail: ... }` payload from `body`.
 */
export interface ApiError {
  status: number;
  body: unknown;
}

/**
 * True while the page is being left: a sign-in redirect, a reload, closing the
 * tab. Requests still in flight then fail on purpose ("Failed to fetch").
 */
let leavingSince: number | null = null;
const LEAVING_GRACE_MS = 5000;
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    leavingSince = Date.now();
  });
  window.addEventListener("pagehide", () => {
    leavingSince = Date.now();
  });
  // Back to this page (bfcache, or a cancelled "leave this page?"): not leaving.
  window.addEventListener("pageshow", () => {
    leavingSince = null;
  });
}

function pageIsLeaving(): boolean {
  return leavingSince !== null && Date.now() - leavingSince < LEAVING_GRACE_MS;
}

/**
 * When the page last became visible (or loaded), for the logged context and
 * for telling a wake-up from a settled session (12 Oct).
 */
let visibleSince: number | null = null;
if (typeof document !== "undefined") {
  visibleSince = document.visibilityState === "visible" ? Date.now() : null;
  document.addEventListener("visibilitychange", () => {
    visibleSince = document.visibilityState === "visible" ? Date.now() : null;
  });
}

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function isVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

/** Resolves once `target` fires `event`, or after `maxMs`; never rejects. */
function waitForEvent(target: EventTarget, event: string, maxMs: number): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      target.removeEventListener(event, done);
      resolve();
    };
    const timer = setTimeout(done, maxMs);
    target.addEventListener(event, done);
  });
}

/** Before a retry: wait for the network if the browser knows it is offline, and for the page. */
async function waitUntilReady(): Promise<void> {
  if (typeof window !== "undefined" && !isOnline()) {
    await waitForEvent(window, "online", READY_WAIT_MAX_MS);
  }
  if (typeof document !== "undefined" && !isVisible()) {
    await waitForEvent(document, "visibilitychange", READY_WAIT_MAX_MS);
  }
}

function failureContext(endpoint: string | undefined): NetworkFailureContext {
  return {
    online: isOnline(),
    leaving: pageIsLeaving(),
    visible: isVisible(),
    sinceVisibleMs: visibleSince === null ? null : Date.now() - visibleSince,
    background: isBackgroundSessionCheck(endpoint, queryClient.getQueryData(["me"]) !== undefined),
  };
}

client.setConfig({
  baseUrl: "/api",
  throwOnError: true,
  // A read with no answer is tried again, backing off, before it counts (9 and
  // 12 Oct): see `@/lib/network-retry`. An arrow, not `globalThis.fetch`
  // itself, so `fetch` keeps its `window` receiver ("Illegal invocation").
  fetch: withNetworkRetry((request) => globalThis.fetch(request), {
    waitUntilReady,
    onRecovered: (request, firstError, info) => {
      logger.warn(
        "[api] no answer at first; a retry worked",
        request.method,
        new URL(request.url).pathname,
        info,
        firstError,
      );
    },
  }),
});

client.interceptors.request.use((request) => {
  logger.debug("[api]", request.method, request.url);
  // On a shared device, who is actually at the keyboard (1 Oct). Empty for
  // everyone else, so a normal session's requests are unchanged.
  for (const [name, value] of Object.entries(kioskHeaders(getKioskPerson()))) {
    request.headers.set(name, value);
  }
  // The tab's demo persona (19 Oct, test systems only): per tab, never a cookie.
  for (const [name, value] of Object.entries(demoPersonaHeaders(getDemoPersona()))) {
    request.headers.set(name, value);
  }
  const screen = currentScreen();
  if (screen) {
    request.headers.set(SCREEN_HEADER, screen);
  }
  return request;
});

client.interceptors.response.use((response) => {
  logger.debug("[api]", response.status, response.url);
  return response;
});

client.interceptors.error.use((error, response, request) => {
  // Transport-level failures (offline, DNS, backend down) reach this
  // interceptor without an HTTP response. Guard it — dereferencing
  // response.status would throw a TypeError that masks the real error.
  // status 0 marks "no response": the query client retries it (unlike
  // 401/403), and error UIs fall back to the detail message.
  if (!response) {
    // The API never saw this one, so the browser logs it (8 Oct). A cancelled
    // request is not a failure; offline or leaving the page is expected, and a
    // background `me` re-check is "try again later" — warnings only (9, 12
    // Oct). A read reaching here was already retried, backing off.
    if (!isAbort(error)) {
      const endpoint = request === undefined ? undefined : new URL(request.url).pathname;
      const message = error instanceof Error ? error.message : "Network error";
      const context = failureContext(endpoint);
      const detail = describeNetworkFailure(context, retryInfoOf(error));
      if (networkFailureAction(context) === "report") {
        reportClientError({ kind: "NETWORK", message, endpoint, detail });
      } else {
        logger.warn("[api] no answer; not logged as an error", endpoint, message, detail);
      }
    }
    return {
      status: 0,
      body: { detail: error instanceof Error ? error.message : "Network error" },
    } satisfies ApiError;
  }
  // The server could not tell who is at the shared device: forget whatever is
  // stored, which sends the person back to the "who are you?" step.
  if (isKioskNameRequired(error)) {
    logger.debug("[kiosk] server asked for the name again");
    clearKioskPerson();
  }
  // The tab's demo persona has ended (expired, or a new sign-in): forget it
  // and read everything again as the admin, rather than act as someone else.
  if (isDemoPersonaInvalid(error)) {
    logger.debug("[demo] the persona ended; back to me");
    clearDemoPersona();
    void queryClient.resetQueries();
  }
  return { status: response.status, body: error } satisfies ApiError;
});

export { client };
